import { useState, useRef } from 'react'
import { CartItemGroups } from '@open-tender/types'

interface UseCollapsibleGroupsProps {
  groups: CartItemGroups
  scrollContainer?: HTMLDivElement | null
  topOffset?: number | null
  startCollapsed?: boolean
}

export const useCollapsibleGroups = ({
  groups,
  scrollContainer,
  topOffset,
  startCollapsed = true
}: UseCollapsibleGroupsProps) => {
  // State to track which groups are collapsed
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    startCollapsed ? new Set(groups.map((g) => g.id.toString())) : new Set()
  )

  // Refs to track group elements for scrolling
  const groupRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})

  // Toggle collapse state for a group
  const toggleGroupCollapse = (groupId: number) => {
    const groupIdStr = groupId.toString()
    const wasCollapsed = collapsedGroups.has(groupIdStr)
    
    setCollapsedGroups((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(groupIdStr)) {
        newSet.delete(groupIdStr)
      } else {
        newSet.add(groupIdStr)
      }
      return newSet
    })

    // Scroll to the group when expanding (after a short delay to allow animation)
    if (wasCollapsed) {
      setTimeout(() => {
        // Check if this is the first group
        const firstGroupId = groups[0]?.id.toString()
        const isFirstGroup = groupIdStr === firstGroupId
        
        if (isFirstGroup && scrollContainer) {
          // Scroll to top for first group
          scrollContainer.scrollTo({
            top: 0,
            behavior: 'smooth'
          })
        } else if (isFirstGroup && !scrollContainer) {
          // Scroll to top of window for first group
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          })
        } else {
          // Regular scroll behavior for other groups
          const groupElement = groupRefs.current[groupIdStr]
          if (groupElement && scrollContainer) {
            const containerRect = scrollContainer.getBoundingClientRect()
            const elementRect = groupElement.getBoundingClientRect()
            const scrollTop = scrollContainer.scrollTop
            const targetScrollTop = scrollTop + elementRect.top - containerRect.top - (topOffset ?? 0) - 20 // 20px offset for better visibility
            
            scrollContainer.scrollTo({
              top: targetScrollTop,
              behavior: 'smooth'
            })
          } else if (groupElement && !scrollContainer) {
            // Fallback to window scrolling if no scroll container
            groupElement.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        }
      }, 100) // Small delay to allow collapse animation to start
    }
  }

  // Check if a group is collapsed
  const isGroupCollapsed = (groupId: number) => {
    return collapsedGroups.has(groupId.toString())
  }

  // Set ref for a group element
  const setGroupRef = (groupId: number, element: HTMLDivElement | null) => {
    groupRefs.current[groupId.toString()] = element
  }

  return {
    collapsedGroups,
    toggleGroupCollapse,
    isGroupCollapsed,
    setGroupRef
  }
}
